// Persists a device's push subscription under the signed-in user so the server
// can fan out notifications to every device. One doc per subscription, keyed by a
// stable hash of the endpoint (endpoints can exceed Firestore's 1500-byte doc-id
// limit, so we can't use the raw endpoint as the id).

import { deleteDoc, doc, serverTimestamp, setDoc } from 'firebase/firestore'
import { db } from '@/services/firebase/config'
import { PUSH_SUBSCRIPTIONS_COLLECTION } from './pushConfig'

// SHA-256 hex of the endpoint — deterministic, so re-subscribing the same device
// overwrites its own doc instead of piling up duplicates.
async function subscriptionId(endpoint: string): Promise<string> {
  const bytes = new TextEncoder().encode(endpoint)
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

// Serialize the browser subscription into the { endpoint, keys: { p256dh, auth } }
// shape the server's web-push send expects.
function toStored(subscription: PushSubscription) {
  const json = subscription.toJSON()
  return {
    endpoint: json.endpoint,
    keys: {
      p256dh: json.keys?.p256dh ?? '',
      auth: json.keys?.auth ?? ''
    }
  }
}

export async function persistSubscription(uid: string, subscription: PushSubscription): Promise<void> {
  const stored = toStored(subscription)
  if (!stored.endpoint) throw new Error('Push subscription has no endpoint')
  const subId = await subscriptionId(stored.endpoint)
  await setDoc(doc(db, 'users', uid, PUSH_SUBSCRIPTIONS_COLLECTION, subId), {
    ...stored,
    userAgent: navigator.userAgent,
    createdAt: serverTimestamp()
  })
}

export async function removeSubscription(uid: string, subscription: PushSubscription): Promise<void> {
  const endpoint = subscription.endpoint
  if (!endpoint) return
  const subId = await subscriptionId(endpoint)
  await deleteDoc(doc(db, 'users', uid, PUSH_SUBSCRIPTIONS_COLLECTION, subId))
}
